import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import FormGroup from '@sharedComponents/FormGroup';
import InputText from '@sharedComponents/InputText';
import Select from '@sharedComponents/Select';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CLIENT_CATEGORIES, getClientTypeOptions, getCsmOptions } from '../../helpers';

const ClientFormPanel = ({ users = [], currentUserUuid = null, onCreate = () => {} } = {}) => {
    const { t } = useTranslation('bo');

    // The current user is the natural CSM of a client they create, when they hold the CSM role; otherwise left empty.
    const defaultCsm = users.some((user) => user.uuid === currentUserUuid) ? currentUserUuid : '';

    const methods = useForm({
        mode: 'onBlur',
        defaultValues: { name: '', reference: '', category: 'D', csmUserId: defaultCsm, clientType: '' },
    });
    const { register, handleSubmit, control, setError, setValue, getFieldState, formState } = methods;

    // Users usually arrive after the first render: keep the default CSM in sync until the user picks one.
    useEffect(() => {
        if (!getFieldState('csmUserId', formState).isDirty) setValue('csmUserId', defaultCsm);
    }, [defaultCsm, getFieldState, setValue, formState]);

    const categoryOptions = CLIENT_CATEGORIES.map((category) => ({ value: category, label: category }));
    const csmOptions = getCsmOptions(t, users);
    const clientTypeOptions = getClientTypeOptions(t);

    const submit = async (data) => {
        try {
            await onCreate({
                name: data.name.trim(),
                reference: data.reference.trim(),
                category: data.category,
                csmUserId: data.csmUserId || null,
                clientType: data.clientType || null,
            });
        } catch (err) {
            // Any other error is already surfaced as a toast by the caller.
            if (err?.status === 409) {
                setError('reference', { type: 'manual', message: t('This client reference already exists') });
            }
        }
    };

    return (
        <FormProvider {...methods}>
            <FlexBox flexDirection="column" gap="m">
                <form onSubmit={handleSubmit(submit)}>
                    <FlexBox flexDirection="column" gap="m">
                        <FormGroup label={t('Name')} errorMsg={formState.errors.name?.message}>
                            <InputText {...register('name', { required: t('Name is required') })} isError={!!formState.errors.name} />
                        </FormGroup>
                        <FormGroup label={t('Reference')} errorMsg={formState.errors.reference?.message}>
                            <InputText
                                {...register('reference', { required: t('Reference is required') })}
                                isError={!!formState.errors.reference}
                            />
                        </FormGroup>
                        <FormGroup label={t('Category')}>
                            <Select control={control} register={{ name: 'category' }} options={categoryOptions} isSearchable={false} />
                        </FormGroup>
                        <FormGroup label={t('CSM')}>
                            <Select control={control} register={{ name: 'csmUserId' }} options={csmOptions} isSearchable />
                        </FormGroup>
                        <FormGroup label={t('Client type')}>
                            <Select control={control} register={{ name: 'clientType' }} options={clientTypeOptions} isSearchable={false} />
                        </FormGroup>
                    </FlexBox>
                </form>
                <FlexBox justifyContent="flex-end">
                    <Button label={t('Create client')} action={handleSubmit(submit)} />
                </FlexBox>
            </FlexBox>
        </FormProvider>
    );
};

export default ClientFormPanel;
